"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.isNumber = isNumber;
exports.compare = compare;
exports.isDecimal = isDecimal;
exports.decimalCountOf = decimalCountOf;
exports.toFixed = toFixed;
var compose_1 = __importDefault(require("lodash/fp/compose"));
var DECIMAL_EXPRESSION = /^-?(([1-9][0-9]*)|([1-9][0-9]*\.[0-9]+)|0|(0\.[0-9]+))$/;
var PLACES_TO_CARRY = 10;
function divideDecimal(source) {
    var _a = source.split('.'), integerPart = _a[0], _b = _a[1], decimalPart = _b === void 0 ? '' : _b;
    return [integerPart, decimalPart];
}
function integerPartOf(source) {
    return divideDecimal(source)[0];
}
function decimalPartOf(source) {
    return divideDecimal(source)[1];
}
function signOf(source) {
    return /^-/.test(source) ? '-' : '';
}
/**
* Determine whether the _source_ is a number or a string represented number.
*
* ```typescript
* isNumber('100') // true
* isNumber('100.00') // true
* isNumber('foo') // false
* ```
* */
function isNumber(source) {
    return !!String(source)
        && !Number.isNaN(source)
        && (typeof source === 'number' || DECIMAL_EXPRESSION.test(String(source)));
}
/**
* Implement the "compareFn" of number. Useful for sort a number based list.
*
* @returns "-1" if first number less than the second number;
* "0" if the two arguments equals each other;
* "1" when the first number greater than the second number.
*
* ```typescript
* compare(1, 0) // 1
* compare(-1, 1) // -1
* compare(100, 100) // 0
* ```
* */
function compare(a, b) {
    if (!isNumber(a))
        throw new Error('The first argument is not a number.');
    if (!isNumber(b))
        throw new Error('The second argument is not a number.');
    if (a < b) {
        return -1;
    }
    if (a > b) {
        return 1;
    }
    return 0;
}
/**
* Determine whether the _source_ is a decimal or a string represented decimal.
*
* ```typescript
* isDecimal('foo') // false
* isDecimal('100') // false
* isDecimal('100.00') // true
* ```
* */
function isDecimal(source) {
    return isNumber(source) && String(source).indexOf('.') > -1;
}
/**
* Get the digit count after the decimal point.
*
* ```typescript
* decimalCountOf('100') // 0
* decimalCountOf('100.00') // 2
* decimalCountOf('foo') // 0
* ```
* */
function decimalCountOf(source) {
    return isDecimal(source) ? divideDecimal(source)[1].length : 0;
}
function higherPlacesOf(integer) {
    return integer.slice(0, Math.max(0, integer.length - PLACES_TO_CARRY));
}
function lowerPlacesOf(integer) {
    return integer.slice(Math.max(0, integer.length - PLACES_TO_CARRY), integer.length);
}
function carryInteger(source) {
    var result = String(parseInt("1".concat(lowerPlacesOf(source)), 10) + 1);
    result = parseInt(result.slice(0, 1), 10) === 2
        ? result.replace('2', '1')
        : result.slice(1, result.length);
    if (higherPlacesOf(source).length === 0) {
        return result;
    }
    if (result.length === lowerPlacesOf(source).length) {
        return [higherPlacesOf(source), result].join('');
    }
    return "".concat(carryInteger(higherPlacesOf(source))).concat(''.padEnd(PLACES_TO_CARRY, '0'));
}
var carriedDecimalPartOf = (0, compose_1.default)(carryInteger, decimalPartOf);
function carry(source) {
    return isDecimal(source)
        ? "".concat(carriedDecimalPartOf(source).length > decimalCountOf(source)
            ? carry(integerPartOf(source))
            : integerPartOf(source), ".").concat(carriedDecimalPartOf(source).length > decimalCountOf(source)
            ? ''.padEnd(decimalCountOf(source), '0')
            : carriedDecimalPartOf(source))
        : carryInteger(source);
}
function roundPowerDigitOf(source, digit) {
    return decimalPartOf(source).slice(digit, digit + 1);
}
/**
* Format a number using fixed-point notation.
*
* Similar as [Number.toFixed](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toFixed), it manipulate on string instead of number.
*
* It support large number which could exceed the Javascript max number limit.
*
* @param digit  The digit number after the decimal point
* @param option.roundUp  Whether round up the result
*
* ```typescript
* toFixed(2)('100') // '100.00'
* toFixed(2)('100.000') // '100.00'
* toFixed(2)('100.009') // '100.01'
* toFixed(2, { roundUp: false })('100.009') // '100.00'
* ```
* */
function toFixed(digit, _a) {
    var _b = _a === void 0 ? { roundUp: true } : _a, _c = _b.roundUp, roundUp = _c === void 0 ? true : _c;
    return function (source) {
        if (!isNumber(source)) {
            return source;
        }
        var result = "".concat(integerPartOf(source).replace(/^-/, '')).concat(digit === 0 ? '' : '.').concat(decimalCountOf(source) > digit
            ? decimalPartOf(source).slice(0, digit)
            : decimalPartOf(source).padEnd(digit, '0'));
        return "".concat(signOf(source)).concat((roundUp
            && roundPowerDigitOf(source, digit)
            && parseInt(roundPowerDigitOf(source, digit), 10) > 4
            ? carry(result)
            : result));
    };
}
